import { DownloadOutlined } from '@ant-design/icons';
import { useInfiniteQuery } from '@tanstack/react-query';
import { App, Button, Drawer, Flex, Table, Typography } from 'antd';
import type { TableProps } from 'antd';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../lib/api-client';
import { orDash } from '../../lib/format';
import { formatMoney } from '../../lib/money';
import { exportSalesLinesCsv, fetchSalesLines, type SalesLine, type SalesLinesFilter } from './api';

const { Text } = Typography;

type Row = SalesLine & { key: string };

export interface SalesLinesDrawerProps {
  /** What to list; null closes the drawer. */
  filter: SalesLinesFilter | null;
  title: string;
  onClose: () => void;
}

/**
 * The AutoCount lines behind one figure: a month of a stream from the chart,
 * or an item group from the table. Every figure on the tab drills to here.
 */
export function SalesLinesDrawer({ filter, title, onClose }: SalesLinesDrawerProps): ReactNode {
  const { t } = useTranslation();
  const { message } = App.useApp();

  const { data, error, isPending, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: ['analytics', 'sales', 'lines', filter],
    queryFn: ({ pageParam }) => fetchSalesLines(filter as SalesLinesFilter, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.hasMore && last.nextCursor !== null ? last.nextCursor : undefined),
    enabled: filter !== null,
  });

  // A line has no identity of its own on the wire; its place in the list is
  // stable because the API pages by date and document.
  const rows: Row[] = (data?.pages.flatMap((page) => page.items) ?? []).map((line, index) => ({
    ...line,
    key: String(index),
  }));

  const download = async (): Promise<void> => {
    if (filter === null) return;
    try {
      const csv = await exportSalesLinesCsv(filter);
      const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `sales-lines-${String(filter.year)}.csv`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      void message.error(t('insights.lines.downloadFailed'));
    }
  };

  const columns: TableProps<Row>['columns'] = [
    {
      title: t('insights.lines.date'),
      key: 'date',
      // A Kuala Lumpur calendar date from AutoCount: shown as is, never shifted.
      render: (_: unknown, r) => <span className="tabular">{dayjs(r.docDate).format('D MMM YYYY')}</span>,
    },
    {
      title: t('insights.lines.document'),
      key: 'doc',
      render: (_: unknown, r) => <Text className="tabular">{`${r.docType} ${r.docNo}`}</Text>,
    },
    { title: t('insights.lines.customer'), key: 'customer', render: (_: unknown, r) => orDash(r.debtorName) },
    {
      title: t('insights.lines.item'),
      key: 'item',
      render: (_: unknown, r) => (
        <Flex vertical>
          <Text className="tabular">{orDash(r.itemCode)}</Text>
          {r.description !== null && <Text type="secondary">{r.description}</Text>}
        </Flex>
      ),
    },
    { title: t('insights.lines.qty'), key: 'qty', align: 'right', render: (_: unknown, r) => r.qty },
    {
      title: t('insights.lines.revenue'),
      key: 'revenue',
      align: 'right',
      render: (_: unknown, r) => formatMoney(r.revenue),
    },
    {
      title: t('insights.lines.cost'),
      key: 'cost',
      align: 'right',
      render: (_: unknown, r) => (r.cost === null ? '—' : formatMoney(r.cost)),
    },
  ];

  return (
    <Drawer
      open={filter !== null}
      title={title}
      onClose={onClose}
      width={960}
      className="nks-action-drawer"
      extra={
        <Button
          icon={<DownloadOutlined />}
          onClick={() => {
            void download();
          }}
        >
          {t('insights.lines.downloadCsv')}
        </Button>
      }
    >
      <Flex vertical gap={12}>
        {error !== null && (
          <Text type="danger">{error instanceof ApiError ? error.message : t('insights.lines.loadFailed')}</Text>
        )}
        <Table<Row>
          rowKey="key"
          size="small"
          pagination={false}
          scroll={{ x: 'max-content' }}
          // The first page only: a later page appends below, so the rows
          // already read stay on screen while it loads.
          loading={filter !== null && isPending}
          locale={{ emptyText: t('insights.lines.empty') }}
          dataSource={rows}
          columns={columns}
        />
        {hasNextPage && (
          <Button
            block
            loading={isFetchingNextPage}
            onClick={() => {
              void fetchNextPage();
            }}
          >
            {t('insights.lines.loadMore')}
          </Button>
        )}
      </Flex>
    </Drawer>
  );
}
